/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Fetcher,
  HttpMethod,
  RequestContext,
  ResponseContext,
} from '@typoas/runtime';
import {
  CreateVenueParams,
  CreateVenueResponse,
  ErrorType,
  GetVenueResponse,
  ShouldVerifyEmailResponse,
  VerifyCodeResponse,
} from './client';

const existingEmails = [
  'test@treatwell.com',
  'test2@treatwell.com',
  'foo@bar.com',
];

const activeCustomers = ['test2@treatwell.com', 'foo@bar.com'];

const mockVenues = [
  {
    name: 'Treatwell',
    address: {
      street: '32 rue du Louvre',
      city: 'Paris',
      postalCode: '75001',
      countryCode: 'FR',
    },
    loginRedirectURL: 'https://connect.treatwell.fr',
  },
  {
    name: 'Treatwell',
    address: {
      street: '15 Fulwood Place',
      city: 'London',
      postalCode: 'WC1V 6HU',
      countryCode: 'GB',
    },
    loginRedirectURL: 'https://connect.treatwell.co.uk',
  },
  {
    name: 'Sam la coupe',
    address: {
      street: '28 rue des Chauves',
      city: 'Lens',
      postalCode: '62300',
      countryCode: 'FR',
    },
    loginRedirectURL: 'https://pro.treatwell.fr/',
  },
  {
    name: 'Sam la coupe est belle',
    address: {
      street: '38 rue des Cheveux',
      city: 'Le Mans',
      postalCode: '72100',
      countryCode: 'FR',
    },
    loginRedirectURL: 'https://pro.treatwell.fr/',
  },
  {
    name: 'Sammy coiffure',
    address: {
      street: '2807 rue de la Coiffure',
      city: 'Mauzé-sur-le-Mignon',
      postalCode: '79170',
      countryCode: 'FR',
    },
  },
];

export function mockGetVenue(email: string): GetVenueResponse {
  if (activeCustomers.includes(email.toLowerCase())) {
    return {
      redirectURL: 'https://connect.treatwell.fr',
    };
  }
  return {
    redirectURL: null,
  };
}

export function mockShouldVerifyEmail(
  email: string,
): ShouldVerifyEmailResponse {
  return { shouldVerifyEmail: existingEmails.includes(email.toLowerCase()) };
}

export function mockVerifyCode(code: string): VerifyCodeResponse {
  return { success: code.length > 0 };
}

/**
 * Mock for the hand-rolled {@code v1VenueCreateVenue} fetch in {@code client.ts}. Exposes the
 * same taken-name / existing-email rejection behaviour the original {@code postSignUp} had,
 * so {@code USE_MOCK_API=true} still stubs venue creation without hitting the network.
 */
export function mockSignUp(params: CreateVenueParams): CreateVenueResponse {
  const errors: CreateVenueResponse['errors'] = [];

  const namesTaken = mockVenues.map(v => v.name);
  if (namesTaken.includes(params.businessName)) {
    errors.push({
      type: ErrorType.ALREADY_EXISTS,
      key: 'businessName',
    });
  }

  if (existingEmails.includes(params.email)) {
    errors.push({
      type: ErrorType.ALREADY_EXISTS,
      key: 'email',
    });
  }

  return {
    errors,
    venueId: errors.length ? null : 1234,
    supplierId: errors.length ? null : 5678,
    communityAccountId: errors.length ? null : 9012,
    extranetAccountId: errors.length ? null : 3456,
  };
}

/**
 * Mock for the hand-rolled {@code validateBusinessName} fetch. Mirrors {@link mockSignUp}'s
 * taken-name set so the user sees the same "already exists" feedback on Next as they would on
 * submit. Country code is accepted for API symmetry but isn't used by the mock (the mock venue
 * set isn't country-scoped).
 */
export function mockValidateBusinessName(businessName: string): {
  supplier: { canCreate: boolean };
  venue: { canCreate: boolean };
} {
  const taken = mockVenues.map(v => v.name).includes(businessName);
  return {
    supplier: { canCreate: !taken },
    venue: { canCreate: !taken },
  };
}

function getResponse(request: RequestContext) {
  // Only GET (legacy /ssi-api/* endpoints) reaches MockFetcher now — POSTs (sign-up) bypass typoas
  // and stub themselves via mockSignUp from the hand-rolled fetch in client.ts.
  if (request.getHttpMethod() === HttpMethod.GET) {
    // @ts-expect-error private field
    return mockGetVenue(request.url.query.email);
  }
  throw new Error(`MockFetcher: unexpected ${request.getHttpMethod()} request`);
}

export class MockFetcher implements Fetcher {
  async send(request: RequestContext): Promise<ResponseContext> {
    const response = await getResponse(request);

    await new Promise(resolve =>
      setTimeout(
        resolve,
        // delay between 1000 and 1150ms
        Math.round(Math.random() * 150) + 1000,
      ),
    );

    return new ResponseContext(
      200,
      { 'content-type': 'application/json' },
      {
        async text() {
          throw new Error('Not implemented');
        },
        async binary() {
          throw new Error('Not implemented');
        },
        async json() {
          return response;
        },
      },
    );
  }
}
